import type { Renderer } from '../gfx/renderer';

export type MouseButton = 0 | 1 | 2;

export interface InputSink {
  onMouseDown?(x: number, y: number, button: MouseButton, e: MouseEvent): boolean | void;
  onMouseUp?(x: number, y: number, button: MouseButton, e: MouseEvent): boolean | void;
  onMouseMove?(x: number, y: number, e: MouseEvent): void;
  onWheel?(dy: number, e: WheelEvent): void;
  onKeyDown?(e: KeyboardEvent): boolean | void;
  onKeyUp?(e: KeyboardEvent): void;
  onText?(text: string): void;
}

/** Tracks pointer/keyboard state in logical (800×600) coordinates and forwards events. */
export class Input {
  x = 400;
  y = 300;
  readonly buttons = new Set<number>();
  readonly keys = new Set<string>();
  shift = false;
  ctrl = false;
  alt = false;
  sink: InputSink | null = null;

  constructor(private readonly r: Renderer, el: HTMLElement) {
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    el.addEventListener('pointerdown', (e) => {
      el.setPointerCapture(e.pointerId);
      this.move(e);
      this.buttons.add(e.button);
      this.sink?.onMouseDown?.(this.x, this.y, e.button as MouseButton, e);
    });
    el.addEventListener('pointerup', (e) => {
      this.move(e);
      this.buttons.delete(e.button);
      this.sink?.onMouseUp?.(this.x, this.y, e.button as MouseButton, e);
    });
    el.addEventListener('pointermove', (e) => {
      this.move(e);
      this.sink?.onMouseMove?.(this.x, this.y, e);
    });
    el.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.sink?.onWheel?.(Math.sign(e.deltaY), e);
      },
      { passive: false },
    );
    window.addEventListener('keydown', (e) => {
      this.mods(e);
      this.keys.add(e.code);
      if (this.sink?.onKeyDown?.(e)) e.preventDefault();
      else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) this.sink?.onText?.(e.key);
      if (['Tab', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'Backspace'].includes(e.key) && document.activeElement === document.body) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => {
      this.mods(e);
      this.keys.delete(e.code);
      this.sink?.onKeyUp?.(e);
    });
    window.addEventListener('blur', () => {
      this.keys.clear();
      this.buttons.clear();
    });
  }

  private mods(e: KeyboardEvent | MouseEvent): void {
    this.shift = e.shiftKey;
    this.ctrl = e.ctrlKey || e.metaKey;
    this.alt = e.altKey;
  }

  private move(e: PointerEvent): void {
    this.mods(e);
    [this.x, this.y] = this.r.toView(e.clientX, e.clientY);
  }
}
